import { MutationTree } from 'vuex';
import {
  UserPreferencesState,
  WorkflowJobPreference,
  WorkflowTreeView,
  TabStateModel,
  UserPreferencesMutationTypes,
  DocumentToStoreTargetDocumentTypeModel,
  Dictionary,
  DocumentsToStorePreference,
  StoreConstants,
  TableSortModel,
  TableFilterModel,
  ColumnConfiguration,
  SearchSplitConfiguration,
  PagesStoreMode
} from '@/types';
import { userPreferences } from './index';
import { defaultMtableState } from '../mtable';

export const mutations: MutationTree<UserPreferencesState[]> = {
  [UserPreferencesMutationTypes.SetJobVerticalSlider](state, payload: any) {
    const loggedInUserId = Number(payload.userId);
    const userIndexPosition = state.findIndex((x: any) => x.userId === loggedInUserId);
    if (userIndexPosition === -1) {
      const userPreference = userPreferences.defaultUserPreferencesState()[0];
      userPreference.userId = loggedInUserId;
      userPreference.workflowJob.workflowJobPreferences[0] = payload.workflowJobPreference;
      state.push(userPreference);
    } else {
      const currentQueueIndexPosition = state[userIndexPosition].workflowJob.workflowJobPreferences.findIndex((y: any) => y.queueId === payload.workflowJobPreference.queueId);
      if (currentQueueIndexPosition === -1) {
        state[userIndexPosition].workflowJob.workflowJobPreferences.push(payload.workflowJobPreference);
      } else {
        state[userIndexPosition].workflowJob.workflowJobPreferences[currentQueueIndexPosition].verticalSider = payload.workflowJobPreference.verticalSider;
      }
    }
  },

  [UserPreferencesMutationTypes.SetWorkflowTreeView](state, payload: any) {
    const loggedInUserId = Number(payload.userId);
    const userIndexPosition = state.findIndex((x: any) => x.userId === loggedInUserId);

    if (userIndexPosition === -1) {
      const workflowTreeView = userPreferences.defaultUserPreferencesState()[0];
      workflowTreeView.userId = loggedInUserId;
      workflowTreeView.workflowTreeView = payload.workflowTreeView;
      state.push(workflowTreeView);
    } else {
      state[userIndexPosition].workflowTreeView = payload.workflowTreeView;
    }
  },

  [UserPreferencesMutationTypes.SetCanGetNextJob](state, payload: any) {
    const loggedInUserId = Number(payload.userId);
    const userIndexPosition = state.findIndex((x: any) => x.userId === loggedInUserId);
    const workflowJobPreferences = userPreferences.defaultWorkflowJobPreference(payload.workflowJobPreference.queueId, null, payload.workflowJobPreference.canGetNextJob);
    if (userIndexPosition === -1) {
      const userPreference = userPreferences.defaultUserPreferencesState()[0];
      userPreference.userId = loggedInUserId;
      userPreference.workflowJob.workflowJobPreferences[0] = workflowJobPreferences;
      state.push(userPreference);
    } else {
      const currentQueueIndexPosition = state[userIndexPosition].workflowJob.workflowJobPreferences.findIndex((y: any) => y.queueId === payload.workflowJobPreference.queueId);

      if (currentQueueIndexPosition === -1) {
        state[userIndexPosition].workflowJob.workflowJobPreferences.push(workflowJobPreferences);
      } else {
        state[userIndexPosition].workflowJob.workflowJobPreferences[currentQueueIndexPosition].canGetNextJob = payload.workflowJobPreference.canGetNextJob;
      }
    }
  },

  [UserPreferencesMutationTypes.SetLastSelectedWorkflowQueue](state, payload: any) {
    const loggedInUserId = Number(payload.userId);
    const userIndexPosition = state.findIndex((x: any) => x.userId === loggedInUserId);

    if (userIndexPosition === -1) {
      const userPreference = userPreferences.defaultUserPreferencesState()[0];
      userPreference.userId = loggedInUserId;
      userPreference.workflowJob.SelectedWorkflowQueueID = payload.selectedQueueId;
      state.push(userPreference);
    } else {
      state[userIndexPosition].workflowJob.SelectedWorkflowQueueID = payload.selectedQueueId;
    }
  },

  [UserPreferencesMutationTypes.SetWorkflowQueueTableStateField](state, payload: any) {
    const loggedInUserId = Number(payload.userId);
    const userIndexPosition = state.findIndex((x: any) => x.userId === loggedInUserId);
    const tableOptions = Object.assign(defaultMtableState(), {
      [payload.workflowQueueTableStatePreference.field]: payload.workflowQueueTableStatePreference.value
    });
    const workflowJobPreferences = userPreferences.defaultWorkflowJobPreference(payload.workflowQueueTableStatePreference.queueId, null, null, tableOptions);
    if (userIndexPosition === -1) {
      const userPreference = userPreferences.defaultUserPreferencesState()[0];
      userPreference.userId = loggedInUserId;
      userPreference.workflowJob.workflowJobPreferences[0] = workflowJobPreferences;
      state.push(userPreference);
    } else {
      const currentQueueIndexPosition = state[userIndexPosition].workflowJob.workflowJobPreferences.findIndex((y: any) => y.queueId === payload.workflowQueueTableStatePreference.queueId);

      if (currentQueueIndexPosition === -1) {
        state[userIndexPosition].workflowJob.workflowJobPreferences.push(workflowJobPreferences);
      } else {
        if (state[userIndexPosition].workflowJob.workflowJobPreferences[currentQueueIndexPosition].tableOptions) {
          Object.assign(state[userIndexPosition].workflowJob.workflowJobPreferences[currentQueueIndexPosition].tableOptions, {
            [payload.workflowQueueTableStatePreference.field]: payload.workflowQueueTableStatePreference.value
          });
        } else {
          state[userIndexPosition].workflowJob.workflowJobPreferences[currentQueueIndexPosition].tableOptions = tableOptions;
        }
      }
    }
  },

  [UserPreferencesMutationTypes.SetDocumentToStoreTargetDocumentType](state, payload: any) {
    const loggedInUserId = Number(payload.userId);
    const userIndexPosition = state.findIndex((x: any) => x.userId === loggedInUserId);
    const targetDocumentTypeModel: DocumentToStoreTargetDocumentTypeModel = payload.targetDocumentTypeModel;
    const targetDocumentTypeData = {
      [targetDocumentTypeModel.sourceDocumentType]: targetDocumentTypeModel.targetDocumentType
    };
    const documentTypeSelectionsData = {
      [payload.cabinetId]: targetDocumentTypeModel.targetDocumentType
    };
    if (userIndexPosition === -1) {
      const userPreference = userPreferences.defaultUserPreferencesState()[0];
      userPreference.userId = loggedInUserId;
      userPreference.documentToStore.targetDocumentTypes = targetDocumentTypeData;
      userPreference.documentToStore.documentTypeSelections[targetDocumentTypeModel.sourceDocumentType] = documentTypeSelectionsData;
      state.push(userPreference);
    } else {
      if (!state[userIndexPosition].documentToStore) {
        state[userIndexPosition].documentToStore = {
          targetDocumentTypes: {} as Dictionary<number>,
          documentTypeSelections: {} as Dictionary<Dictionary<number>>,
          tableFilters: {
            includeBatchNumber: false,
            pinFilter: false,
            searchTerm: '',
            showMultiSelect: false
          },
          canGetNextDocument: false as boolean,
          allPages: false,
          pagesStoreMode: PagesStoreMode.ManualPageSelection
        };
      }
      const targetDocumentTypes = { ...state[userIndexPosition].documentToStore.targetDocumentTypes };

      state[userIndexPosition].documentToStore.targetDocumentTypes = {
        ...targetDocumentTypes,
        ...targetDocumentTypeData
      };

      if (!state[userIndexPosition].documentToStore?.documentTypeSelections) {
        state[userIndexPosition].documentToStore.documentTypeSelections = {} as Dictionary<Dictionary<number>>;
      }
      if (!state[userIndexPosition].documentToStore.documentTypeSelections[targetDocumentTypeModel.sourceDocumentType]) {
        state[userIndexPosition].documentToStore.documentTypeSelections[targetDocumentTypeModel.sourceDocumentType] = {} as Dictionary<number>;
      }

      if (payload.cabinetId == null || payload.cabinetId === '') {
        return;
      }

      const existingDocumentTypeSelections = {
        ...state[userIndexPosition].documentToStore.documentTypeSelections[targetDocumentTypeModel.sourceDocumentType]
      };

      const updatedDocumentTypeSelections = {
        ...existingDocumentTypeSelections,
        ...documentTypeSelectionsData
      };

      state[userIndexPosition].documentToStore.documentTypeSelections[targetDocumentTypeModel.sourceDocumentType] = updatedDocumentTypeSelections;
    }
  },

  [UserPreferencesMutationTypes.RemoveDocumentTypeSelection](state, payload: any) {
    const loggedInUserId = Number(payload.userId);
    const userIndexPosition = state.findIndex((x: any) => x.userId === loggedInUserId);
    const documentTypeIdToRemove = payload.documentTypeId;

    if (userIndexPosition === -1) {
      return;
    }

    if (!state[userIndexPosition].documentToStore) {
      return;
    }

    if (!state[userIndexPosition].documentToStore?.documentTypeSelections) {
      return;
    }

    if (!state[userIndexPosition].documentToStore.documentTypeSelections[payload.sourceDocumentType]) {
      return;
    }

    const existingDocumentTypeSelections = {
      ...state[userIndexPosition].documentToStore.documentTypeSelections[payload.sourceDocumentType]
    };

    const updatedDocumentTypeSelections = Object.assign(
      {},
      ...Object.entries(existingDocumentTypeSelections)
        .filter(([k, v]) => v !== documentTypeIdToRemove)
        .map(([k, v]) => ({ [k]: v }))
    );

    state[userIndexPosition].documentToStore.documentTypeSelections[payload.sourceDocumentType] = updatedDocumentTypeSelections;
  },

  [UserPreferencesMutationTypes.SetDocumentToStoreCanGetNextDocument](state, payload: any) {
    const loggedInUserId = Number(payload.userId);
    const userIndexPosition = state.findIndex((x: any) => x.userId === loggedInUserId);
    const canGetNextDocument: boolean = payload.canGetNextDocument;
    if (userIndexPosition === -1) {
      const userPreference = userPreferences.defaultUserPreferencesState()[0];
      userPreference.userId = loggedInUserId;
      userPreference.documentToStore.canGetNextDocument = canGetNextDocument;
      state.push(userPreference);
      return;
    }

    if (!state[userIndexPosition].documentToStore) {
      state[userIndexPosition].documentToStore = {
        targetDocumentTypes: {} as Dictionary<number>,
        documentTypeSelections: {} as Dictionary<Dictionary<number>>,
        canGetNextDocument: {} as boolean,
        tableFilters: {
          includeBatchNumber: false,
          pinFilter: false,
          searchTerm: '',
          showMultiSelect: false
        },
        allPages: false,
        pagesStoreMode: PagesStoreMode.ManualPageSelection
      };
    }
    state[userIndexPosition].documentToStore.canGetNextDocument = payload.canGetNextDocument;
  },

  [UserPreferencesMutationTypes.SetPinFilter](state, payload: any) {
    const loggedInUserId = Number(payload.userId);
    const userIndexPosition = state.findIndex((x: any) => x.userId === loggedInUserId);
    const workflowJobPreferences = userPreferences.defaultWorkflowJobPreference(
      payload?.workflowJobPreference?.queueId,
      payload?.workflowJobPreference?.verticalSider,
      payload?.workflowJobPreference?.canGetNextJob,
      payload?.workflowJobPreference?.tableOptions,
      payload?.workflowJobPreference?.pinFilter
    );
    if (userIndexPosition === -1) {
      const userPreference = userPreferences.defaultUserPreferencesState()[0];
      userPreference.userId = loggedInUserId;
      userPreference.workflowJob.workflowJobPreferences[0] = workflowJobPreferences;
      state.push(userPreference);
    } else {
      const currentQueueIndexPosition = state[userIndexPosition].workflowJob.workflowJobPreferences.findIndex((y: any) => y.queueId === payload.workflowJobPreference.queueId);

      if (currentQueueIndexPosition === -1) {
        state[userIndexPosition].workflowJob.workflowJobPreferences.push(workflowJobPreferences);
      } else {
        state[userIndexPosition].workflowJob.workflowJobPreferences[currentQueueIndexPosition].pinFilter = payload.workflowJobPreference.pinFilter;
      }
    }
  },

  [UserPreferencesMutationTypes.SetIsHorizontalTabUp](state, payload: any) {
    const loggedInUserId = Number(payload.userId);
    const userIndexPosition = state.findIndex((x: any) => x.userId === loggedInUserId);
    if (userIndexPosition === -1) {
      const userPreference = userPreferences.defaultUserPreferencesState()[0];
      userPreference.userId = loggedInUserId;
      userPreference.workflowJob.workflowJobPreferences[0].isHorizontalTabUp = payload.horizontalTabVisibility.isHorizontalTabUp;
      state.push(userPreference);
    } else {
      const currentQueueIndexPosition = state[userIndexPosition].workflowJob.workflowJobPreferences.findIndex((y: any) => y.queueId === payload.horizontalTabVisibility.queueId);
      if (currentQueueIndexPosition === -1) {
        const workflowJobPreferences = userPreferences.defaultWorkflowJobPreference();
        workflowJobPreferences.queueId = payload.horizontalTabVisibility.queueId;
        workflowJobPreferences.isHorizontalTabUp = payload.horizontalTabVisibility.isHorizontalTabUp;
        state[userIndexPosition].workflowJob.workflowJobPreferences.push(workflowJobPreferences);
      } else {
        state[userIndexPosition].workflowJob.workflowJobPreferences[currentQueueIndexPosition].isHorizontalTabUp = payload.horizontalTabVisibility.isHorizontalTabUp;
      }
    }
  },
  [UserPreferencesMutationTypes.UpdateVuexPersistedState](state) {
    const defaultUserPreference = userPreferences.defaultUserPreferencesState()[0];
    for (let index = 0; index < state.length; index++) {
      const userPreference = state[index];
      state[index] = Object.assign(defaultUserPreference, userPreference);
    }
  },
  [UserPreferencesMutationTypes.SetDocumentsToStoreTableSortProperties](state, payload: any) {
    const loggedInUserId = Number(payload.userId);
    let userIndexPosition = state.findIndex((x: any) => x.userId === loggedInUserId);
    const fieldName = payload.key.fieldName;
    if (userIndexPosition === -1) {
      const userPreference = userPreferences.defaultUserPreferencesState()[0];
      userPreference.userId = loggedInUserId;
      state.push(userPreference);
      userIndexPosition = 0;
    } else if (!state[userIndexPosition].documentsToStore.tableSortPropertiesPreference) {
      state[
        userIndexPosition
        // tslint:disable-next-line:max-line-length
      ].documentsToStore.tableSortPropertiesPreference = userPreferences.defaultUserPreferencesState()[0].documentsToStore.tableSortPropertiesPreference;
    }
    fieldName === 'sortBy'
      ? (state[userIndexPosition].documentsToStore.tableSortPropertiesPreference.sortBy = payload.key.fieldValue)
      : (state[userIndexPosition].documentsToStore.tableSortPropertiesPreference.sortDesc = payload.key.fieldValue);
  },
  [UserPreferencesMutationTypes.AddSuppressedConfirmation](state, payload: any) {
    const loggedInUserId = Number(payload.userId);
    const userIndexPosition = state.findIndex((x: any) => x.userId === loggedInUserId);

    if (userIndexPosition === -1) {
      const userPreference = userPreferences.defaultUserPreferencesState()[0];
      userPreference.userId = loggedInUserId;
      userPreference.suppressedConfirmations.push(payload.key);
      state.push(userPreference);
    } else {
      if (!state[userIndexPosition].suppressedConfirmations) {
        state[userIndexPosition].suppressedConfirmations = [];
      }
      if (!state[userIndexPosition].suppressedConfirmations.includes(payload.key)) {
        state[userIndexPosition].suppressedConfirmations.push(payload.key);
      }
    }
  },
  [UserPreferencesMutationTypes.RemoveSuppressedConfirmation](state, payload: any) {
    const loggedInUserId = Number(payload.userId);
    const userIndexPosition = state.findIndex((x: any) => x.userId === loggedInUserId);

    if (userIndexPosition === -1 || !state[userIndexPosition].suppressedConfirmations) {
      return;
    }
    const index = state[userIndexPosition].suppressedConfirmations.indexOf(payload.key);
    if (index !== -1) {
      state[userIndexPosition].suppressedConfirmations.splice(index, 1);
    }
  },
  [UserPreferencesMutationTypes.SetDocumentsToStoreVerticalSlider](state, payload: any) {
    const loggedInUserId = Number(payload.userId);
    const userIndexPosition = state.findIndex((x: any) => x.userId === loggedInUserId);
    if (userIndexPosition === -1) {
      const userPreference = userPreferences.defaultUserPreferencesState()[0];
      userPreference.userId = loggedInUserId;
      userPreference.documentsToStore.documentsToStorePreference[0] = payload.documentsToStorePreference;
      state.push(userPreference);
    } else {
      const documentsToStorePreference = state[userIndexPosition]?.documentsToStore?.documentsToStorePreference;
      if (!documentsToStorePreference) {
        state[userIndexPosition].documentsToStore.documentsToStorePreference[0] = payload.documentsToStorePreference;
        return;
      }
      const currentDocumentTypeIndexPosition = documentsToStorePreference.findIndex((y: any) => y.documentType === payload.documentsToStorePreference.documentType);

      if (currentDocumentTypeIndexPosition === -1) {
        documentsToStorePreference.push(payload.documentsToStorePreference);
      } else {
        documentsToStorePreference[currentDocumentTypeIndexPosition] = payload.documentsToStorePreference;
      }
    }
  },
  [UserPreferencesMutationTypes.SetDocumentToStoreTableFilters](state, payload: any) {
    const loggedInUserId = Number(payload.userId);
    const userIndexPosition = state.findIndex((x: any) => x.userId === loggedInUserId);
    if (userIndexPosition === -1) {
      const userPreference = userPreferences.defaultUserPreferencesState()[0];
      userPreference.userId = loggedInUserId;
      userPreference.documentToStore.tableFilters = payload.tableFilters;
      state.push(userPreference);
    } else {
      state[userIndexPosition].documentToStore.tableFilters = payload.tableFilters;
    }
  },
  [StoreConstants.UserPreferences.Mutations.SetSortOptions](state, { userId, queueId, sortOptions }: { userId: string; queueId: string; sortOptions: TableSortModel[] }) {
    const loggedInUserId = Number(userId);
    const userIndexPosition = state.findIndex((x: any) => x.userId === loggedInUserId);
    if (userIndexPosition === -1) {
      const userPreference = userPreferences.defaultUserPreferencesState()[0];
      userPreference.userId = loggedInUserId;
      userPreference.workflowQueue.sortOptions[queueId] = sortOptions;
      state.push(userPreference);
    } else {
      const existingWorkflowQueueState = state[userIndexPosition].workflowQueue ?? userPreferences.defaultUserPreferencesState()[0].workflowQueue;
      const existingSortOptions = existingWorkflowQueueState.sortOptions ?? {};
      const updatedSortingOptions = { ...existingSortOptions, [queueId]: [...sortOptions] };
      state[userIndexPosition].workflowQueue = { ...existingWorkflowQueueState, sortOptions: updatedSortingOptions };
    }
  },
  [StoreConstants.UserPreferences.Mutations.SetFilterOptions](state, { userId, queueId, filterOptions }: { userId: string; queueId: string; filterOptions: TableFilterModel[] }) {
    const loggedInUserId = Number(userId);
    const userIndexPosition = state.findIndex((x: any) => x.userId === loggedInUserId);
    if (userIndexPosition === -1 && filterOptions) {
      const userPreference = userPreferences.defaultUserPreferencesState()[0];
      userPreference.userId = loggedInUserId;
      userPreference.workflowQueue.filterOptions[queueId] = filterOptions;
      state.push(userPreference);
    } else {
      const existingWorkflowQueueState = state[userIndexPosition].workflowQueue ?? userPreferences.defaultUserPreferencesState()[0].workflowQueue;
      const existingFilterOptions = existingWorkflowQueueState.filterOptions ?? {};
      const updatedFilteringOptions = { ...existingFilterOptions };
      if (filterOptions) {
        updatedFilteringOptions[queueId] = [...filterOptions];
      } else {
        delete updatedFilteringOptions[queueId];
      }
      state[userIndexPosition].workflowQueue = { ...existingWorkflowQueueState, filterOptions: updatedFilteringOptions };
    }
  },
  [StoreConstants.UserPreferences.Mutations.SetRetainFilterOptions](state, { userId, queueId, retainFilterOptions }: { userId: string; queueId: string; retainFilterOptions: boolean }) {
    const loggedInUserId = Number(userId);
    const userIndexPosition = state.findIndex((x: any) => x.userId === loggedInUserId);
    if (userIndexPosition === -1 && retainFilterOptions) {
      const userPreference = userPreferences.defaultUserPreferencesState()[0];
      userPreference.userId = loggedInUserId;
      userPreference.workflowQueue.retainFilterOptions[queueId] = retainFilterOptions;
      state.push(userPreference);
    } else {
      const existingWorkflowQueueState = state[userIndexPosition].workflowQueue ?? userPreferences.defaultUserPreferencesState()[0].workflowQueue;
      const existingRetainFilterOptions = existingWorkflowQueueState.retainFilterOptions ?? {};
      const updatedRetainFilterOptions = { ...existingRetainFilterOptions };
      if (retainFilterOptions) {
        updatedRetainFilterOptions[queueId] = retainFilterOptions;
      } else {
        delete updatedRetainFilterOptions[queueId];
      }
      state[userIndexPosition].workflowQueue = { ...existingWorkflowQueueState, retainFilterOptions: updatedRetainFilterOptions };
    }
  },
  [StoreConstants.UserPreferences.Mutations.SetPinSort](state, { userId, queueId, pinSort }: { userId: string; queueId: string; pinSort: boolean }) {
    const loggedInUserId = Number(userId);
    const userIndexPosition = state.findIndex((x: any) => x.userId === loggedInUserId);
    if (userIndexPosition === -1) {
      const userPreference = userPreferences.defaultUserPreferencesState()[0];
      userPreference.userId = loggedInUserId;
      userPreference.workflowQueue.pinSort[queueId] = pinSort;
      state.push(userPreference);
    } else {
      const existingWorkflowQueueState = state[userIndexPosition].workflowQueue ?? userPreferences.defaultUserPreferencesState()[0].workflowQueue;
      const existingPinSort = existingWorkflowQueueState.pinSort ?? {};
      const updatedPinSort = { ...existingPinSort, [queueId]: pinSort };
      state[userIndexPosition].workflowQueue = { ...existingWorkflowQueueState, pinSort: updatedPinSort };
    }
  },
  [StoreConstants.UserPreferences.Mutations.SetPagesStoreMode](state, { userId, pagesStoreMode }: { userId: string; pagesStoreMode: PagesStoreMode }) {
    const loggedInUserId = Number(userId);
    const userIndexPosition = state.findIndex((x: any) => x.userId === loggedInUserId);
    if (userIndexPosition === -1) {
      const userPreference = userPreferences.defaultUserPreferencesState()[0];
      userPreference.userId = loggedInUserId;
      userPreference.documentToStore.pagesStoreMode = pagesStoreMode;
      state.push(userPreference);
    } else {
      const existingDocumentToStoreState = state[userIndexPosition].documentToStore ?? userPreferences.defaultUserPreferencesState()[0].documentToStore;
      state[userIndexPosition].documentToStore = { ...existingDocumentToStoreState, pagesStoreMode };
    }
  },
  [StoreConstants.UserPreferences.Mutations.SetColumnConfigurations](
    state,
    { userId, queueId, columnConfigurations }: { userId: string; queueId: string; columnConfigurations: ColumnConfiguration[] }
  ) {
    const loggedInUserId = Number(userId);
    const userIndexPosition = state.findIndex((x: any) => x.userId === loggedInUserId);
    if (userIndexPosition === -1) {
      const userPreference = userPreferences.defaultUserPreferencesState()[0];
      userPreference.userId = loggedInUserId;
      userPreference.workflowQueue.columnConfigurations[queueId] = columnConfigurations;
      state.push(userPreference);
    } else {
      const existingWorkflowQueueState = state[userIndexPosition].workflowQueue ?? userPreferences.defaultUserPreferencesState()[0].workflowQueue;
      const existingColumnConfiguration = existingWorkflowQueueState.columnConfigurations ?? {};
      const updatedColumnConfiguration = { ...existingColumnConfiguration, [queueId]: columnConfigurations };
      state[userIndexPosition].workflowQueue = { ...existingWorkflowQueueState, columnConfigurations: updatedColumnConfiguration };
    }
  },
  [StoreConstants.UserPreferences.Mutations.SetEmailToMe](state, { userId, convertToPdf, dontAskAgain }: { userId: string; convertToPdf: boolean; dontAskAgain: boolean }) {
    const loggedInUserId = Number(userId);
    const userIndexPosition = state.findIndex((x: any) => x.userId === loggedInUserId);
    if (userIndexPosition === -1) {
      const userPreference = userPreferences.defaultUserPreferencesState()[0];
      userPreference.userId = loggedInUserId;
      userPreference.emailToMe = { convertToPdf, dontAskAgain };
      state.push(userPreference);
    } else {
      state[userIndexPosition].emailToMe = { convertToPdf, dontAskAgain };
    }
  },
  [StoreConstants.UserPreferences.Mutations.SetSearchOptions](state, { userId, cabinets, documentTypes }: { userId: string; cabinets: string[]; documentTypes: number[] }) {
    const loggedInUserId = Number(userId);
    const userIndexPosition = state.findIndex((x: any) => x.userId === loggedInUserId);
    if (userIndexPosition === -1) {
      const userPreference = userPreferences.defaultUserPreferencesState()[0];
      userPreference.userId = loggedInUserId;
      userPreference.search = { ...userPreference.search, cabinets, documentTypes };
      state.push(userPreference);
    } else {
      state[userIndexPosition].search = { ...state[userIndexPosition].search, cabinets, documentTypes };
    }
  },
  [StoreConstants.UserPreferences.Mutations.SetSearchPinSort](state, { userId, pinSort }: { userId: string; pinSort: boolean }) {
    const loggedInUserId = Number(userId);
    const userIndexPosition = state.findIndex((x: any) => x.userId === loggedInUserId);
    if (userIndexPosition === -1) {
      const userPreference = userPreferences.defaultUserPreferencesState()[0];
      userPreference.userId = loggedInUserId;
      userPreference.search = { ...userPreference.search, pinSort };
      state.push(userPreference);
    } else {
      state[userIndexPosition].search = { ...state[userIndexPosition].search, pinSort };
    }
  },
  [StoreConstants.UserPreferences.Mutations.SetSearchPinFilter](state, { userId, pinFilter }: { userId: string; pinFilter: boolean }) {
    const loggedInUserId = Number(userId);
    const userIndexPosition = state.findIndex((x: any) => x.userId === loggedInUserId);
    if (userIndexPosition === -1) {
      const userPreference = userPreferences.defaultUserPreferencesState()[0];
      userPreference.userId = loggedInUserId;
      userPreference.search = { ...userPreference.search, pinFilter };
      state.push(userPreference);
    } else {
      state[userIndexPosition].search = { ...state[userIndexPosition].search, pinFilter };
    }
  },
  [StoreConstants.UserPreferences.Mutations.SetSearchSplitConfiguration](state, { userId, splitConfiguration }: { userId: string; splitConfiguration: SearchSplitConfiguration }) {
    const loggedInUserId = Number(userId);
    const userIndexPosition = state.findIndex((x: any) => x.userId === loggedInUserId);
    if (userIndexPosition === -1) {
      const userPreference = userPreferences.defaultUserPreferencesState()[0];
      userPreference.userId = loggedInUserId;
      userPreference.search = { ...userPreference.search, splitConfiguration };
      state.push(userPreference);
    } else {
      state[userIndexPosition].search = { ...state[userIndexPosition].search, splitConfiguration };
    }
  },
  [StoreConstants.UserPreferences.Mutations.SetIncludeDocumentContent](state, { userId, includeDocumentContent }: { userId: string; includeDocumentContent: boolean }) {
    const loggedInUserId = Number(userId);
    const userIndexPosition = state.findIndex((x: any) => x.userId === loggedInUserId);
    if (userIndexPosition === -1) {
      const userPreference = userPreferences.defaultUserPreferencesState()[0];
      userPreference.userId = loggedInUserId;
      userPreference.search.includeDocumentContent = includeDocumentContent;
      state.push(userPreference);
    } else {
      state[userIndexPosition].search.includeDocumentContent = includeDocumentContent;
    }
  }
};
